import { useIncidentStatusList, usePriorityOptions } from "@features/ax/hooks";
import { logEvent } from "@shared/analytics/analytics";
import { DeleteConfirmationDialog } from "@shared/components/deleteComformationModal";
import { DynamicForm } from "@shared/components/DynamicForm";
import { GenericTable } from "@shared/components/GenericTable";
import { HoverRevealText } from "@shared/components/HoverRevealText";
import { PageHeader } from "@shared/components/PageHeader";
import { useToast } from "@shared/hooks/use-toast";
import { FormField, FormService } from "@shared/services/FormServices";
import { TableAction, TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES, PAGE_SIZE } from "@shared/utils/constants";
import { Building, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetIncidents, useIgnoreIncident } from "../../hooks";
import { SummaryCards } from "./summaryCards";
interface IncidentsFiltersForm {
  status: string;
  priority: string;
  date_from: string;
  date_to: string;
  search: string;
}
interface Incident {
  id: string;
  incidentNo: string;
  statusDisplay: string;
  priorityDisplay: string;
  description: string;
  location: string;
  customerName: string;
  incidentAriseDate: string;
}
export const Landing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { data: priorityList } = usePriorityOptions();
  const { data: statusList } = useIncidentStatusList();

  const [isIgnoreDialogOpen, setIsIgnoreDialogOpen] = useState(false);
  const [incidentToIgnore, setIncidentToIgnore] = useState<Incident | null>(
    null
  );

  const { mutateAsync: ignoreIncident, isPending: isIgnoring } =
    useIgnoreIncident();

  // const { mutateAsync: deleteIncident, isPending: isDeleting } = useDeleteIncident();
  const urlParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      page: parseInt(searchParams.get("page") || "1", 10),
      search: searchParams.get("query") || "",
      status: searchParams.get("status") || "",
      priority: searchParams.get("priority") || "",
      date_from: searchParams.get("date_from") || "",
      date_to: searchParams.get("date_to") || "",
    };
  }, [location.search]);
  const [currentPage, setCurrentPage] = useState(urlParams.page);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<IncidentsFiltersForm>({
    status: urlParams.status,
    priority: urlParams.priority,
    date_from: urlParams.date_from,
    date_to: urlParams.date_to,
    search: urlParams.search,
  });

  const handleIgnoreClick = (incident: Incident) => {
    if (incident.statusDisplay === "Ignored") {
      toast({
        title: "Error",
        description: "Incident is already ignored ",
        variant: "destructive",
      });
      return;
    }
    setIncidentToIgnore(incident);
    setIsIgnoreDialogOpen(true);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "High Priority":
        return "bg-red-100 text-red-800 border-red-200";
      case "Critical":
        return "bg-red-100 text-red-800 border-red-200";
      case "Medium Priority":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "Low Priority":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusColor = (statusDisplay: string) => {
    const status = statusDisplay.toLowerCase();
    if (status.includes("new")) {
      return "bg-blue-100 text-blue-800 border-blue-200";
    } else if (status.includes("progress")) {
      return "bg-purple-100 text-purple-800 border-purple-200";
    } else if (status.includes("pending")) {
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    } else if (status.includes("resolved") || status.includes("completed")) {
      return "bg-green-100 text-green-800 border-green-200";
    }
    return "bg-gray-100 text-gray-800 border-gray-200";
  };

  const handleConfirmIgnore = async () => {
    if (!incidentToIgnore) return;
    try {
      logEvent("Incident Ignored", {
        incidentId: incidentToIgnore.id,
        incidentNo: incidentToIgnore.incidentNo,
        page: "Incidents Landing",
        action: "confirm_ignore",
      });

      await ignoreIncident({
        id: incidentToIgnore.id,
        payload: { status: "4" },
      });

      toast({
        title: "Success",
        description: "Incident ignored successfully",
        variant: "default",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to ignore incident",
        variant: "destructive",
      });
    } finally {
      setIsIgnoreDialogOpen(false);
      setIncidentToIgnore(null);
    }
  };

  const handleCloseIgnoreDialog = () => {
    setIsIgnoreDialogOpen(false);
    setIncidentToIgnore(null);
  };

  useEffect(() => {
    setCurrentPage(urlParams.page);
    setFilters({
      status: urlParams.status,
      priority: urlParams.priority,
      date_from: urlParams.date_from,
      date_to: urlParams.date_to,
      search: urlParams.search,
    });
  }, [urlParams]);

  const priorityOptions = useMemo(() => {
    return (
      priorityList?.map((assetType: any) => ({
        value: String(assetType.key),
        label: assetType.value,
      })) || []
    );
  }, [priorityList]);

  const statusOptions = useMemo(() => {
    return (
      statusList?.result?.map((assetType: any) => ({
        value: String(assetType.key),
        label: assetType.value,
      })) || []
    );
  }, [statusList]);

  const apiParams = useMemo(() => {
    return {
      ...filters,
      page: currentPage,
      limit: PAGE_SIZE,
    };
  }, [filters, currentPage]);

  const { data: incidentData } = useGetIncidents(apiParams);

  const filterFields: FormField[] = useMemo(
    () => [
      {
        name: "status",
        type: "select",
        label: "Status",
        placeholder: "Select status...",
        classes: FIELD_CLASSES,
        options: [{ value: "", label: "All Statuses" }, ...statusOptions],
      },
      {
        name: "priority",
        type: "select",
        label: "Priority",
        placeholder: "Select priority...",
        classes: FIELD_CLASSES,
        options: [{ value: "", label: "All Priorities" }, ...priorityOptions],
      },
      {
        name: "date_from",
        type: "datepicker",
        label: "Date From",
        placeholder: "Select start date",
        classes: FIELD_CLASSES,
      },
      {
        name: "date_to",
        type: "datepicker",
        label: "Date To",
        placeholder: "Select end date",
        classes: FIELD_CLASSES,
      },
    ],
    [statusOptions, priorityOptions]
  );

  const filterForm = FormService.createForm({
    fields: filterFields,
  });

  useEffect(() => {
    filterForm.reset({
      status: urlParams.status,
      priority: urlParams.priority,
      date_from: urlParams.date_from,
      date_to: urlParams.date_to,
      search: urlParams.search,
    });
  }, [urlParams, filterForm]);

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case "new":
        return "default";
      case "asset identified":
        return "secondary";
      case "ignored":
        return "destructive";
      default:
        return "outline";
    }
  };

  const columns: TableColumn[] = [
    {
      key: "incidentNo",
      header: "Incidents ID",
      type: "custom",
      headerTooltip: "A unique system-generated identifier.",
      render: (value: string) => <div className="ml-6">{value}</div>,
    },
    {
      key: "asset",
      header: "Asset ID/Name",
      headerTooltip:
        "If available, linked asset (e.g., Pipe ID, Hydrant, Valve). Assets help trace recurring issues and plan maintenance.",
      type: "custom",
      render: (value: any) => (
        <div>
          <div className="ml-6">{value?.assetNo || "N/A"}</div>
          <div className="text-sm text-gray-500 ml-6">
            {value?.assetName || "N/A"}
          </div>
        </div>
      ),
    },
    {
      key: "location",
      header: "Location",
      headerTooltip: "Primary address or site of the reported incident.",
      type: "custom",
      render: (value) => <HoverRevealText text={value} />,
    },
    {
      key: "description",
      header: "Description",
      headerTooltip:
        "Short description of the reported issue provided during creation (e.g., 'Leak reported on hydrant'). Detailed notes appear inside the incident record.",
      type: "custom",
      render: (value) => <HoverRevealText text={value} />,
    },
    {
      key: "priorityDisplay",
      header: "Priority",
      type: "badge",
      headerTooltip: (
        <ul>
          <li>Priority level of the incident:</li>
          <li>
            <strong>High:</strong> Immediate response required
            (service-affecting).
          </li>
          <li>
            <strong>Medium:</strong> Same-day response needed (quality issue).
          </li>
          <li>
            <strong>Low:</strong> Non-urgent, response within 24–48 hrs.
          </li>
          <li>
            Priority impacts assignment, SLA tracking, and escalation workflows.
          </li>
        </ul>
      ),
      render: (value: string) => (
        <Badge className={getPriorityColor(value)}>{value}</Badge>
      ),
    },
    {
      key: "statusDisplay",
      header: "Status",
      type: "badge",
      sortable: true,
      width: "w-36",
      badgeConfig: {
        getClassName: (value: string) => getStatusColor(value),
      },
      headerTooltip: "Shows the current stage of the incident workflow.",
    },
    {
      key: "lastModifiedUserRemoteName",
      header: "Reported By",
      headerTooltip:
        "Person or system that logged the incident. Can be a customer (via web portal, app, or call center), a technician, or an automated system alert. Used for contact and audit purposes.",
      type: "custom",
      render: (value: string) => <div className="ml-6">{value || "N/A"}</div>,
    },
    {
      key: "incidentAriseDate",
      header: "Date Reported",
      headerTooltip: "The exact date the incident was recorded in the system.",
      type: "custom",
      render: (value: string) => <div className="ml-6">{value || "N/A"}</div>,
    },
  ];

  const actions: TableAction[] = [
    {
      label: "View Details",
      onClick: (row) => {
        logEvent("View Incident Details", {
          incidentId: row.id,
          incidentNo: row.incidentNo,
          page: "Incidents Landing",
          action: "view_details",
        });
        navigate(`/ax/field-alerts/incidents/view/${row.id}`);
      },
      icon: "Eye",
      variant: "outline",
    },
    {
      label: "Edit Incident",
      onClick: (row) => {
        logEvent("Edit Incident", {
          incidentId: row.id,
          incidentNo: row.incidentNo,
          page: "Incidents Landing",
          action: "edit_incident",
        });
        navigate(`/ax/field-alerts/incidents/create/edit/${row.id}`);
      },
      variant: "outline",
    },
    {
      label: "Ignore Incident",
      onClick: (row) => {
        logEvent("Ignore Incident Initiated", {
          incidentId: row.id,
          incidentNo: row.incidentNo,
          page: "Incidents Landing",
          action: "open_ignore_dialog",
        });
        handleIgnoreClick(row);
      },
      disabled: (row) => row.statusDisplay.toLowerCase().includes("ignored"),
      variant: "outline",
      icon: "X",
    },
  ];

  const updateUrl = (
    updates: Partial<{
      page: number;
      query: string;
      status: string;
      priority: string;
      date_from: string;
      date_to: string;
    }>
  ) => {
    const searchParams = new URLSearchParams(location.search);
    Object.entries(updates).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (value !== "" && value !== 0) {
          searchParams.set(key, String(value));
        } else {
          searchParams.delete(key);
        }
      } else {
        searchParams.delete(key);
      }
    });
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const handleSearch = (searchValue: string) => {
    setFilters({
      ...filters,
      search: searchValue,
    });
    updateUrl({ query: searchValue, page: 1 });
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    updateUrl({ page });
  };

  const handleExport = () => {
    // TODO: Implement actual export logic
  };

  const handleFiltersSubmit = (formData: IncidentsFiltersForm) => {
    setShowFilters(false);
    updateUrl({
      status: formData.status,
      priority: formData.priority,
      date_from: formData.date_from,
      date_to: formData.date_to,
      query: formData.search,
      page: 1,
    });
  };

  const handleFiltersReset = () => {
    const defaultFilters: IncidentsFiltersForm = {
      status: "",
      priority: "",
      date_from: "",
      date_to: "",
      search: "",
    };
    setFilters(defaultFilters);
    filterForm.reset(defaultFilters);
    setCurrentPage(1);
    const searchParams = new URLSearchParams();
    searchParams.set("page", "1");
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const FilterPanel = () => (
    <DynamicForm
      fields={filterFields}
      form={filterForm}
      onSubmit={handleFiltersSubmit}
      onReset={handleFiltersReset}
      config={{
        showSubmitButton: true,
        showResetButton: true,
        submitButtonText: "Apply Filters",
        resetButtonText: "Clear All",
        layout: "vertical",
        spacing: "medium",
        classes: {
          buttonContainer: "flex justify-end gap-3 pt-6 border-gray-200 mt-8",
        },
      }}
      gridColumns={4}
      responsiveColumns={{ sm: 1, md: 2, lg: 3, xl: 4 }}
      className="space-y-4"
    />
  );

  const totalItems = incidentData?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startItem = totalItems > 0 ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);
  return (
    <div className="p-2">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <PageHeader
            title="Incidents"
            subtitle="Monitor and manage all reported incidents"
          />
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => {
              logEvent("Report New Incident", {
                page: "Incidents Landing",
                action: "create_incident",
              });
              navigate("/ax/field-alerts/incidents/create");
            }}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Incidents
          </Button>
        </div>
      </div>

      <SummaryCards />
      <DeleteConfirmationDialog
        isOpen={isIgnoreDialogOpen}
        onClose={handleCloseIgnoreDialog}
        onConfirm={handleConfirmIgnore}
        title="Ignore Incident"
        itemName={incidentToIgnore?.incidentNo}
        description={`Are you sure you want to ignore incident "${incidentToIgnore?.incidentNo}"? This will mark the incident as ignored and remove it from active tracking.`}
        confirmText="Ignore Incident"
        isLoading={isIgnoring}
      />

      <div className="mt-8">
        <GenericTable
          data={incidentData?.results || []}
          columns={columns}
          rowKey="id"
          searchPlaceholder="Search incidents by ID, or reporter..."
          searchParamName="query"
          searchDebounceMs={300}
          searchValue={filters.search}
          onSearch={handleSearch}
          actions={actions}
          actionsType="dropdown"
          emptyMessage="No incidents found"
          emptyIcon={<Building className="h-8 w-8 text-gray-400" />}
          filterPanel={<FilterPanel />}
          showFilters={showFilters}
          onShowFiltersChange={setShowFilters}
          // onExport={handleExport}
          exportLoading={false}
          pagination={{
            currentPage,
            totalPages,
            totalItems,
            itemsPerPage: PAGE_SIZE,
            startItem,
            endItem,
          }}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};
export default Landing;
